import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ClientSessionService } from '@brighthub/uisession';
import { Button } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { CopilotContextComposerService } from '@brighthub/copilot';
import {
  ClientProfileSection,
  ClientProfileItem,
} from '../client-profile.model';

/** Section with precomputed display values and editability for template use (no methods in template). */
interface SectionWithDisplayValues extends ClientProfileSection {
  hasEditableItems: boolean;
  items: (ClientProfileItem & { displayValue: string | number | null })[];
}
import { ClientProfileService } from '../client-profile.service';
import { ClientProfileEditService } from '../client-profile-edit.service';
import { GenericResponseCode } from '@brighthub/api';
import { ClientContactService } from '../../client-contact/client-contact.service';

const PHONE_KEYS = new Set(['workPhone', 'mobilePhone']);
const EMAIL_KEYS = new Set(['personalEmail', 'workEmail']);

@Component({
  selector: 'bai-client-profile',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, Button],
  providers: [DialogService, ClientProfileEditService],
  templateUrl: './client-profile.component.html',
  styleUrl: './client-profile.component.scss',
})

export class ClientProfileComponent {
  private _session = inject(ClientSessionService);
  private _profileService = inject(ClientProfileService);
  private _destroyRef = inject(DestroyRef);
  private _contactService = inject(ClientContactService);
  private _copilotComposer = inject(CopilotContextComposerService);
  readonly editService = inject(ClientProfileEditService);

  /** Sections from API; empty when no client or loading/error */
  readonly sections = signal<ClientProfileSection[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  /** Selected client GUID; derived from session */
  private _guid = computed(() => {
    const session = this._session.selectedSession();
    return session?.subjectData?.guid ?? null;
  });

  /** Sections with precomputed display values and hasEditableItems so the template never calls methods. */
  readonly sectionsWithDisplayValues = computed<SectionWithDisplayValues[]>(() => {
    const s = this.sections();
    const edits = this.editService.editValues();
    return s.map((section) => {
      const sectionId = section.id ?? section.title;
      const items = section.items.map((item) => {
        const key = this._itemEditKey(sectionId, item);
        const displayValue =
          edits[key] !== undefined ? edits[key] : item.value;
        return { ...item, displayValue };
      });
      const hasEditableItems = items.some((i) => i.editable);
      return { ...section, items, hasEditableItems };
    });
  });

  constructor() {
    effect((onCleanup) => {
      const guid = this._guid();
  
      // reset baseline state for new guid
      this.error.set(null);
      this.sections.set([]);
  
      if (!guid) {
        this.loading.set(false);
        return;
      }
  
      this.loading.set(true);
  
      const sub = this._profileService.getProfile(guid).subscribe({
        next: (res) => {
          this.loading.set(false);
  
          if (
            res.result.code === GenericResponseCode.SERVICE_OK &&
            res.payload?.sections
          ) {
            this.sections.set(res.payload.sections);
            this.error.set(null);
          } else {
            this.sections.set([]);
            this.error.set(res.result.message || 'Failed to load profile');
          }
        },
        error: (err) => {
          this.loading.set(false);
          this.sections.set([]);
          this.error.set(err?.message ?? 'Failed to load profile');
        },
      });
  
      // ✅ cancels the previous request when guid changes (or component destroyed)
      onCleanup(() => sub.unsubscribe());
    });
  }
  

  private _itemEditKey(sectionId: string, item: ClientProfileItem): string {
    return `${sectionId ?? 'section'}-${item.key ?? item.label}`;
  }

  isPhoneKey(key: string | undefined): boolean {
    return !!key && PHONE_KEYS.has(key);
  }

  isEmailKey(key: string | undefined): boolean {
    return !!key && EMAIL_KEYS.has(key);
  }

  normalizePhone(value: string): string {
    return this._contactService.normalizePhone(value);
  }

  addProfileItemToCopilot(
    sectionTitle: string,
    item: ClientProfileItem & { displayValue: string | number | null }
  ): void {
    const value = item.displayValue ?? '—';
    const sourceId = `${sectionTitle}:${item.key ?? item.label}`;

    this._copilotComposer.addContext({
      sourceType: 'profile-item',
      sourceId,
      label: `Profile: ${this._truncate(`${sectionTitle} · ${item.label}`, 20)}`,
      fullText:
        `[Profile | ${sourceId}] ` +
        `Section: ${sectionTitle}; ` +
        `Field: ${item.label}; ` +
        `Value: ${value}`,
    });
  }

  private _truncate(value: string, max = 20): string {
    if (value.length <= max) return value;
    return `${value.slice(0, max)}…`;
  }

}
